import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Users, Info } from "lucide-react";
import { useState } from "react";
import { Cliente } from "@/features/clientes/types/cliente";
import { useClienteFormSimplificado } from "@/features/clientes/hooks/useClienteFormSimplificado";
import { usePermissao } from "@/features/auth/hooks/usePermissao";
import { useProfileSwitcher } from "@/hooks/useProfileSwitcher";
import { PersonalInfoStepSimplificado } from "./steps/PersonalInfoStepSimplificado";
import { QualificacaoStep } from "./steps/QualificacaoStep";
import { AdditionalInfoStep } from "./steps/AdditionalInfoStep";
import { PipelineFormProvider } from "@/components/form/PipelineFormContext";

interface ClienteFormSimplificadoProps {
  formData?: Cliente;
  onSuccess?: (cliente: Cliente) => void;
  isDialog?: boolean;
}

export function ClienteFormSimplificado({ formData, onSuccess, isDialog = false }: ClienteFormSimplificadoProps) {
  const { getCurrentProfile } = useProfileSwitcher();
  const currentRole = getCurrentProfile;
  usePermissao();

  const {
    form,
    onSubmit,
    isSubmitting,
    isSuccess,
  } = useClienteFormSimplificado(formData, onSuccess);

  const [activeStep, setActiveStep] = useState<'basic' | 'qualificacao' | 'observacoes'>('basic');

  if (isSuccess) {
    return (
      <Card className="p-6">
        <div className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <Users className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold">Cliente cadastrado com sucesso!</h2>
          <p className="text-muted-foreground">O lead foi qualificado e está disponível para os relacionadores.</p>
        </div>
      </Card>
    );
  }

  const steps = [
    { id: 'basic', title: 'Informações Básicas' },
    { id: 'qualificacao', title: 'Qualificação' },
    { id: 'observacoes', title: 'Observações' },
  ];

  const currentStepIndex = steps.findIndex(step => step.id === activeStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  const nextStep = () => {
    if (activeStep === 'basic') {
      // Validar campos básicos antes de avançar (nome e telefone são obrigatórios)
      const nome = form.getValues('nome');
      const tel1 = form.getValues('tel1');

      if (!nome || nome.trim() === '') {
        form.setError('nome', { message: 'Nome é obrigatório' });
        return;
      }

      if (!tel1 || tel1.trim() === '') {
        form.setError('tel1', { message: 'Telefone é obrigatório' });
        return;
      }

      // Limpar erros se tudo estiver ok
      form.clearErrors(['nome', 'tel1']);
      setActiveStep('qualificacao');
    } else if (activeStep === 'qualificacao') {
      form.trigger(['temEntrada', 'valorParcelaOrcamento', 'tempoProcura', 'prioridade']);
      const errors = form.formState.errors;

      if (errors.valorParcelaOrcamento || errors.tempoProcura || errors.prioridade) {
        return;
      }
      // Se tem entrada, validar valor da entrada
      if (form.watch('temEntrada') && errors.valorEntrada) {
        return;
      }
      setActiveStep('observacoes');
    }
  };

  const prevStep = () => {
    if (activeStep === 'observacoes') {
      setActiveStep('qualificacao');
    } else if (activeStep === 'qualificacao') {
      setActiveStep('basic');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLastStep) {
      form.handleSubmit(onSubmit)();
    }
  };

  return (
    <div className="mx-auto">
      {/* Header */}
      {!isDialog && (
        <div className="bg-white rounded-t-xl border border-b-0 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Users className="h-6 w-6 text-mendes-blue" />
              <div>
                <h1 className="text-2xl font-bold">Qualificação de Lead - Relacionador</h1>
                <p className="text-muted-foreground">Cadastro simplificado para qualificação de leads</p>
              </div>
            </div>
            <Badge variant="secondary">{currentRole}</Badge>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex items-center gap-2 flex-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${index <= currentStepIndex
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                      }`}
                  >
                    {index + 1}
                  </div>
                  <span
                    className={`text-sm ${index === currentStepIndex
                      ? 'font-semibold text-blue-600'
                      : index < currentStepIndex
                        ? 'text-gray-600'
                        : 'text-gray-400'
                      }`}
                  >
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 mx-2 ${index < currentStepIndex ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Card className={isDialog ? "border-0 shadow-none" : "rounded-t-none"}>
        <PipelineFormProvider variant={isDialog ? "pipeline" : "default"}>
        <Form {...form}>
          <form onSubmit={handleSubmit} className={`space-y-6 ${isDialog ? 'p-3' : 'p-6'}`}>
            {!isDialog && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Como relacionador, você está qualificando um lead. Preencha as informações básicas e as perguntas obrigatórias de qualificação.
              </AlertDescription>
            </Alert>
            )}

            {/* Step 1: Informações Básicas */}
            {activeStep === 'basic' && (
              <div className="space-y-6">
                <PersonalInfoStepSimplificado form={form} />
              </div>
            )}

            {/* Step 2: Qualificação */}
            {activeStep === 'qualificacao' && (
              <div className="space-y-6">
                <QualificacaoStep form={form} />
              </div>
            )}

            {/* Step 3: Observações */}
            {activeStep === 'observacoes' && (
              <div className="space-y-6">
                <AdditionalInfoStep form={form} />
              </div>
            )}

            {/* Navigation Buttons */}
            <div className={`flex justify-between pt-4 border-t ${isDialog ? 'gap-2' : ''}`}>
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                disabled={isFirstStep || isSubmitting}
              >
                Voltar
              </Button>

              {!isLastStep ? (
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    nextStep();
                  }}
                  disabled={isSubmitting}
                >
                  Próximo
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    isDialog ? 'Salvar alterações' : 'Salvar Lead Qualificado'
                  )}
                </Button>
              )}
            </div>
          </form>
        </Form>
        </PipelineFormProvider>
      </Card>
    </div>
  );
}
